import { LoadingBlur, NoResultsMessage } from '@ics-portal/guardian';
import { type ReactNode, useMemo, useState } from 'react';
import { type ZoneModels } from '@models/Zone';
import { isDefined } from '@utils/isDefined';
import { Header, Panel } from './components';

interface StandardProfilesProps {
    standards: ZoneModels.Standard[];
    loading: boolean;
    selectedProfileIds: string[];
    onProfileChange: (profileId: string, checked: boolean) => void;
    onStandardClear: (standardId: string) => void;
    allowedProfileIds?: string[];
    zoneId?: string;
    projectId?: string;
    readonly?: boolean;
    invalid?: boolean;
    singleSelect?: boolean;
}

export function StandardProfiles({
    standards,
    loading,
    selectedProfileIds,
    onProfileChange,
    onStandardClear,
    allowedProfileIds,
    zoneId,
    projectId,
    readonly = false,
    invalid = false,
    singleSelect = false,
}: StandardProfilesProps): ReactNode {
    const [searchTerm, setSearchTerm] = useState('');
    const [expandedStandardId, setExpandedStandardId] = useState<string | null>(
        null,
    );

    const allowedProfileIdSet = useMemo(
        () => new Set(allowedProfileIds ?? []),
        [allowedProfileIds],
    );

    const filteredStandards = useMemo(() => {
        const lowerSearch = searchTerm.toLowerCase();
        return standards.filter((standard) => {
            const profiles = (standard.standardProfiles ?? []).filter(
                isDefined,
            );
            const profileMatch = allowedProfileIds
                ? profiles.some((p) => allowedProfileIdSet.has(p.id))
                : profiles.length > 0;
            return (
                profileMatch &&
                (!searchTerm ||
                    standard.name.toLowerCase().includes(lowerSearch))
            );
        });
    }, [standards, searchTerm, allowedProfileIds, allowedProfileIdSet]);

    return (
        <LoadingBlur loading={loading}>
            <div className="flex flex-column gap-2">
                <Header onSearchChange={setSearchTerm} />

                {filteredStandards.map((standard) => {
                    const hasGuidance = (standard.standardProfiles ?? [])
                        .filter(isDefined)
                        .some((profile) => allowedProfileIdSet.has(profile.id));
                    const isExpanded = expandedStandardId === standard.id;
                    return (
                        <Panel
                            key={standard.id}
                            standard={standard}
                            selectedProfileIds={selectedProfileIds}
                            onProfileChange={onProfileChange}
                            onStandardClear={onStandardClear}
                            allowedProfileIds={allowedProfileIds}
                            zoneId={zoneId}
                            projectId={projectId}
                            readonly={readonly}
                            hasGuidance={hasGuidance}
                            invalid={invalid}
                            singleSelect={singleSelect}
                            collapsed={!isExpanded}
                            onToggle={() => {
                                setExpandedStandardId(
                                    isExpanded ? null : standard.id,
                                );
                            }}
                        />
                    );
                })}

                {!loading && !filteredStandards.length && <NoResultsMessage />}
            </div>
        </LoadingBlur>
    );
}
